import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tabs,
  TimePicker,
  Tooltip,
  message
} from "antd";
import dayjs from "dayjs";
import { FolderOpenOutlined, PlusOutlined, SaveOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { getSetting, loadRuntimeSettings } from "@core/runtime-settings";
import { createSprPred, fetchSprPred, fetchSprPredList, matchSprPredPhones } from "../../sprs/pred/spr-pred-api";
import SprPred from "../../sprs/pred/spr-pred";
import PhoneInput, { phoneRule } from "../../sprs/pred/phone-input";
import { fetchSprNumberCategories, fetchSprNumberList } from "../../sprs/number/spr-number-api";
import { docExpSellBook, docExpSellBookPreview, fetchBronFreeCategories, fetchBronFreeMest, fetchDocExpLookups } from "./doc-exp-api";
import {
  VID_MESTA_OPTIONS,
  formatMoneyRub,
  normalizeLookups,
  phoneKey,
  toApiDate,
  toApiDateTime
} from "./doc-exp-utils";
import infoSrc from "./assets/info.svg";
import plusSrc from "./assets/plus.svg";
import xSrc from "./assets/x.svg";
import "./doc-exp-sell-book-modal.css";

const DRAWER_WIDTH_KEY = "planOnline.docExp.sellBookDrawer.width.v2";
const HEADER_STORAGE_KEY = "planOnline.docExp.sellBook.header.v1";
const HEADER_FIELDS = new Set([
  "idNumberCat",
  "vidPutevki",
  "vidPitania",
  "idSkidka",
  "timeBeg",
  "timeEnd",
  "dateBeg",
  "dateEnd",
  "dateBron"
]);
const DRAWER_MIN_WIDTH = 440;
const DRAWER_MAX_WIDTH = 720;
const DRAWER_DEFAULT_WIDTH = 520;
const SCREEN_MARGIN = 80;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return DRAWER_MAX_WIDTH;
  return Math.min(DRAWER_MAX_WIDTH, Math.max(DRAWER_MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DRAWER_DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(DRAWER_MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DRAWER_DEFAULT_WIDTH);
  } catch {
    return DRAWER_DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const parseTime = (value, fallback) => {
  const t = dayjs(typeof value === "string" ? value : "", "HH:mm");
  return t.isValid() ? t : dayjs(fallback, "HH:mm");
};

const nightsBetween = (beg, end) => {
  if (!beg?.isValid?.() || !end?.isValid?.()) return 0;
  return end.startOf("day").diff(beg.startOf("day"), "day");
};

const pickLookupValue = (options, ...candidates) => {
  const list = options || [];
  for (const raw of candidates) {
    const n = Number(raw);
    if (!n) continue;
    if (list.some((o) => Number(o.value) === n)) return n;
  }
  return undefined;
};

const loadHeaderDraft = () => {
  try {
    const raw = localStorage.getItem(HEADER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const nights = Number(parsed.nights);
    return {
      idNumberCat: Number(parsed.idNumberCat) || 0,
      vidPutevki: Number(parsed.vidPutevki) || 0,
      vidPitania: Number(parsed.vidPitania) || 0,
      idSkidka: Number(parsed.idSkidka) || 0,
      timeBeg: typeof parsed.timeBeg === "string" ? parsed.timeBeg : "14:00",
      timeEnd: typeof parsed.timeEnd === "string" ? parsed.timeEnd : "12:00",
      nights: Number.isFinite(nights) && nights > 0 ? Math.min(nights, 365) : 7,
      dateBron: typeof parsed.dateBron === "string" ? parsed.dateBron : null
    };
  } catch {
    return null;
  }
};

const saveHeaderDraft = (values) => {
  if (!values) return;
  const nights = nightsBetween(values.dateBeg, values.dateEnd);
  try {
    localStorage.setItem(
      HEADER_STORAGE_KEY,
      JSON.stringify({
        idNumberCat: Number(values.idNumberCat) || 0,
        vidPutevki: Number(values.vidPutevki) || 0,
        vidPitania: Number(values.vidPitania) || 0,
        idSkidka: Number(values.idSkidka) || 0,
        timeBeg: values.timeBeg?.isValid?.() ? values.timeBeg.format("HH:mm") : "14:00",
        timeEnd: values.timeEnd?.isValid?.() ? values.timeEnd.format("HH:mm") : "12:00",
        nights: nights > 0 ? nights : 7,
        dateBron: values.dateBron?.isValid?.() ? values.dateBron.format("YYYY-MM-DD") : null
      })
    );
  } catch {
    /* ignore quota / private mode */
  }
};

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

const emptyGuest = () => ({
  name: "",
  phone: "",
  idPred: 0,
  predName: "",
  vidMesta: 0,
  vidLech: undefined,
  isBaby: 0,
  age: undefined,
  komment: ""
});

const ageYearsToDateBird = (age) => {
  const n = Number(age);
  if (!Number.isFinite(n) || n < 0) return null;
  return toApiDate(dayjs().startOf("day").subtract(Math.round(n), "year"));
};

const emptyPrice = () => ({
  syma: 0,
  discount: 0,
  symaWithoutDiscount: 0,
  days: 0,
  symaDay: 0,
  symaAcc: 0,
  symaPit: 0,
  symaLech: 0,
  error: ""
});

const normalizePrice = (res) => ({
  syma: Number(pick(res, "syma", "Syma")) || 0,
  discount: Number(pick(res, "discount", "Discount")) || 0,
  symaWithoutDiscount: Number(pick(res, "symaWithoutDiscount", "SymaWithoutDiscount")) || 0,
  days: Number(pick(res, "days", "Days")) || 0,
  symaDay: Number(pick(res, "symaDay", "SymaDay")) || 0,
  symaAcc: Number(pick(res, "symaAcc", "SymaAcc")) || 0,
  symaPit: Number(pick(res, "symaPit", "SymaPit")) || 0,
  symaLech: Number(pick(res, "symaLech", "SymaLech")) || 0,
  error: String(pick(res, "error", "Error") || "").trim()
});

const isAbortError = (e) => e?.name === "AbortError" || /abort/i.test(e?.message || "");

const PriceSummary = ({ price, loading }) => {
  const error = String(price?.error || "").trim();
  return (
    <div
      className={`sell-book__price${loading ? " sell-book__price--loading" : ""}${
        error ? " sell-book__price--error" : ""
      }`}
    >
      <div className="sell-book__price-break">
        <div className="sell-book__price-item">
          <span>Проживание</span>
          <strong>{formatMoneyRub(price.symaAcc)}</strong>
        </div>
        <div className="sell-book__price-item">
          <span>Питание</span>
          <strong>{formatMoneyRub(price.symaPit)}</strong>
        </div>
        <div className="sell-book__price-item">
          <span>Лечение</span>
          <strong>{formatMoneyRub(price.symaLech)}</strong>
        </div>
      </div>
      <div className="sell-book__price-grid">
        <div className="sell-book__price-item">
          <span>Без скидки</span>
          <strong>{formatMoneyRub(price.symaWithoutDiscount)}</strong>
        </div>
        <div className="sell-book__price-item">
          <span>Скидка</span>
          <strong>{formatMoneyRub(price.discount)}</strong>
        </div>
        <div className="sell-book__price-item sell-book__price-item--total">
          <span>Итого</span>
          <strong>{formatMoneyRub(price.syma)}</strong>
        </div>
        <div className="sell-book__price-item">
          <span>Дней</span>
          <strong>{price.days || 0}</strong>
        </div>
        <div className="sell-book__price-item">
          <span>В день</span>
          <strong>{formatMoneyRub(price.symaDay)}</strong>
        </div>
      </div>
      {error ? (
        <div className="sell-book__price-error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
};

const guestTabTitle = (guest, index) => {
  const name = String(guest?.name || "").trim();
  return name || `Гость ${index + 1}`;
};

const predOptionsFrom = (data) =>
  (Array.isArray(data) ? data : pick(data, "items", "Items") || [])
    .map((row) => ({
      value: Number(pick(row, "idPred", "IdPred")) || 0,
      label: [pick(row, "name", "Name"), pick(row, "phone", "Phone")].filter(Boolean).join(" · ")
    }))
    .filter((o) => o.value);

/** Выбор контрагента из справочника с поиском по имени/телефону. */
const PredSelect = ({ value, label, onChange, onCreate, placeholder, disabled, size }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(0);
  const search = (q) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setLoading(true);
      fetchSprPredList({ q, take: 30 })
        .then((data) => setOptions(predOptionsFrom(data)))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 250);
  };
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const opts = value && !options.some((o) => o.value === value)
    ? [{ value, label: label || `Контрагент №${value}` }, ...options]
    : options;
  return (
    <Select
      showSearch
      allowClear
      size={size}
      value={value || undefined}
      options={opts}
      filterOption={false}
      onSearch={search}
      onOpenChange={(opened) => opened && search("")}
      loading={loading}
      disabled={disabled}
      placeholder={placeholder}
      notFoundContent={loading ? "Загрузка…" : "Не найдено"}
      onChange={(v) => onChange(v || 0)}
      style={{ width: "100%" }}
      popupRender={(menu) => (
        <>
          {menu}
          {onCreate ? (
            <div className="sell-book__pred-create">
              <Button type="link" size="small" icon={<PlusOutlined />}
                onMouseDown={(e) => e.preventDefault()} onClick={onCreate}>
                Новый контрагент
              </Button>
            </div>
          ) : null}
        </>
      )}
    />
  );
};

const guestFromPred = (pred) => ({
  name: pred?.name || "",
  phone: pred?.phone || "",
  idPred: Number(pred?.idPred) || 0,
  predName: pred?.name || ""
});

const GuestCard = ({ field, form, lookups, onOpenPred }) => {
  const isBaby = Form.useWatch(["clients", field.name, "isBaby"], form);
  const idPred = Form.useWatch(["clients", field.name, "idPred"], form) || 0;
  const predName = Form.useWatch(["clients", field.name, "predName"], form);
  const phone = Form.useWatch(["clients", field.name, "phone"], form);
  const headerIdClient = Form.useWatch("idClient", form);
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const lechOptions = (lookups.vidLech || []).filter((o) => o.value);

  const patch = (values) =>
    form.setFieldValue(["clients", field.name], { ...(form.getFieldValue(["clients", field.name]) || {}), ...values });

  const applyPred = async (id) => {
    if (!id) {
      patch({ idPred: 0, predName: "" });
      return;
    }
    try {
      patch(guestFromPred(await fetchSprPred(id)));
    } catch (e) {
      message.error(e.message || "Не удалось загрузить контрагента");
    }
  };

  const key = phoneKey(phone);
  useEffect(() => {
    if (idPred || key.length < 5) {
      setMatch(null);
      return undefined;
    }
    const ac = new AbortController();
    const t = setTimeout(() => {
      matchSprPredPhones([phone], ac.signal)
        .then((rows) => { if (!ac.signal.aborted) setMatch(Array.isArray(rows) && rows[0] ? rows[0] : null); })
        .catch(() => {});
    }, 500);
    return () => { clearTimeout(t); ac.abort(); };
  }, [idPred, key, phone]);

  const saveToSpr = async () => {
    const c = form.getFieldValue(["clients", field.name]) || {};
    if (!(c.name || "").trim()) {
      message.warning("Укажите ФИО гостя");
      return;
    }
    setBusy(true);
    try {
      if (match) {
        Modal.confirm({
          title: "Телефон уже есть в справочнике",
          content: `Контрагент «${match.name}» имеет такой же телефон. Привязать гостя к нему?`,
          okText: "Привязать",
          cancelText: "Отмена",
          onOk: () => applyPred(match.idPred)
        });
        return;
      }
      const saved = await createSprPred({
        vid: 0,
        name: c.name.trim(),
        phone: c.phone || "",
        dateBird: c.isBaby ? ageYearsToDateBird(c.age) : null
      });
      patch(guestFromPred(saved));
      message.success("Контрагент сохранён в справочник");
    } catch (e) {
      message.error(e.message || "Не удалось сохранить в справочник");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sell-book__guest">
      <Form.Item name={[field.name, "idPred"]} hidden><InputNumber /></Form.Item>
      <Form.Item name={[field.name, "predName"]} hidden><Input /></Form.Item>
      <div className="sell-book__guest-pred">
        <PredSelect
          value={idPred}
          label={predName}
          placeholder="Контрагент из справочника"
          onChange={applyPred}
          onCreate={() => onOpenPred(null, field.name)}
        />
        <Space size={0}>
          {idPred ? (
            <Tooltip title="Открыть карточку контрагента">
              <Button type="text" size="small" icon={<FolderOpenOutlined />} aria-label="Открыть контрагента"
                onClick={() => onOpenPred(idPred, field.name)} />
            </Tooltip>
          ) : (
            <Tooltip title="Сохранить гостя в справочник">
              <Button type="text" size="small" icon={<SaveOutlined />} aria-label="В справочник" loading={busy} onClick={saveToSpr} />
            </Tooltip>
          )}
          {!idPred ? (
            <Tooltip title={headerIdClient ? "Привязать гостя к контрагенту из шапки путёвки" : "В шапке путёвки контрагент не выбран"}>
              <Button type="text" size="small" icon={<UserSwitchOutlined />} disabled={!headerIdClient}
                aria-label="Контрагент из шапки путёвки" onClick={() => applyPred(headerIdClient)} />
            </Tooltip>
          ) : null}
        </Space>
      </div>
      {!idPred && match ? (
        <Alert
          type="warning"
          showIcon
          className="sell-book__guest-match"
          message={<>В справочнике есть контрагент с таким телефоном: <b>{match.name}</b></>}
          action={<Button size="small" type="primary" onClick={() => applyPred(match.idPred)}>Привязать</Button>}
        />
      ) : null}
      <div className="sell-book__guest-row">
        <Form.Item name={[field.name, "name"]} className="sell-book__guest-name">
          <Input placeholder="ФИО" disabled={Boolean(idPred)} title={idPred ? "ФИО берётся из карточки контрагента" : undefined} />
        </Form.Item>
        <Form.Item name={[field.name, "phone"]} className="sell-book__guest-phone" rules={[phoneRule]} validateTrigger="onBlur">
          <PhoneInput disabled={Boolean(idPred)} />
        </Form.Item>
        <Form.Item name={[field.name, "vidLech"]} className="sell-book__guest-lech">
          <Select
            allowClear
            placeholder="Лечение"
            options={lechOptions}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
      </div>
      <div className="sell-book__guest-meta">
        <Form.Item name={[field.name, "vidMesta"]} className="sell-book__guest-mesta">
          <Select options={VID_MESTA_OPTIONS} />
        </Form.Item>
        <div className="sell-book__guest-side">
          <Form.Item
            name={[field.name, "isBaby"]}
            valuePropName="checked"
            getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}
            className="sell-book__guest-baby"
          >
            <Checkbox>Ребёнок</Checkbox>
          </Form.Item>
          <div className="sell-book__guest-age">
            <span>Возраст</span>
            <Form.Item name={[field.name, "age"]} className="sell-book__guest-age-field">
              <InputNumber min={0} max={17} controls={false} disabled={!isBaby} />
            </Form.Item>
            <span>лет</span>
          </div>
        </div>
      </div>
      <Form.Item name={[field.name, "komment"]} className="sell-book__guest-comment">
        <Input placeholder="Комментарий" />
      </Form.Item>
    </div>
  );
};

const GuestsPanel = ({ fields, add, remove, form, lookups, onOpenPred }) => {
  const fieldKeys = fields.map((field) => String(field.key));
  const keysSig = fieldKeys.join("|");
  const [activeKey, setActiveKey] = useState(fieldKeys[0] || "");
  const pendingAdd = useRef(false);
  const clients = Form.useWatch("clients", form) || [];

  useEffect(() => {
    const keys = keysSig ? keysSig.split("|") : [];
    if (!keys.length) return;
    if (pendingAdd.current) {
      pendingAdd.current = false;
      setActiveKey(keys[keys.length - 1]);
      return;
    }
    setActiveKey((current) => (keys.includes(current) ? current : keys[keys.length - 1]));
  }, [keysSig]);

  const addGuest = () => {
    pendingAdd.current = true;
    add(emptyGuest());
  };

  return (
    <Tabs
      className="sell-book__guest-tabs"
      activeKey={activeKey}
      onChange={setActiveKey}
      destroyOnHidden={false}
      tabBarExtraContent={
        <button type="button" className="sell-book__guest-tab-add" onClick={addGuest} aria-label="Добавить гостя">
          <img src={plusSrc} alt="" />
        </button>
      }
      items={fields.map((field, index) => ({
        key: String(field.key),
        forceRender: true,
        label: (
          <span className="sell-book__guest-tab-label">
            <span className="sell-book__guest-tab-name">{guestTabTitle(clients[field.name], index)}</span>
            {fields.length > 1 ? (
              <button
                type="button"
                className="sell-book__guest-tab-remove"
                aria-label="Удалить гостя"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  remove(field.name);
                }}
              >
                <img src={xSrc} alt="" />
              </button>
            ) : null}
          </span>
        ),
        children: <GuestCard field={field} form={form} lookups={lookups} onOpenPred={onOpenPred} />
      }))}
    />
  );
};

const requiredSelect = (messageText) => ({
  validator: (_, value) =>
    value ? Promise.resolve() : Promise.reject(new Error(messageText))
});

/**
 * Продажа + бронь.
 * - mode "category" (журнал): категория → сервер подбирает свободный номер (Delphi TFrmDocExpCreateBron).
 * - mode "room" (шахматка «Продать»): конкретный номер из выделения.
 */
const DocExpSellBookModal = ({
  open,
  preset,
  mode = "category",
  asDrawer = false,
  onCancel,
  onDone
}) => {
  const byRoom = mode === "room" || Boolean(preset?.idNumber);
  const [form] = Form.useForm();
  const [numbers, setNumbers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lookups, setLookups] = useState({
    vidPutevki: [],
    vidPitania: [],
    vidLech: []
  });
  const [hint, setHint] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [drawerSize, setDrawerSize] = useState(loadWidth);
  const [price, setPrice] = useState(emptyPrice);
  const [priceLoading, setPriceLoading] = useState(false);
  const headerSaveTimer = useRef(null);
  const skipHeaderSaveRef = useRef(false);
  /** { idPred, guestField? } — карточка контрагента: редактирование/создание. */
  const [predDialog, setPredDialog] = useState(null);
  const idClientWatch = Form.useWatch("idClient", form);
  const idClientNameWatch = Form.useWatch("idClientName", form);

  useEffect(() => () => clearTimeout(headerSaveTimer.current), []);

  useEffect(() => {
    if (!open || !asDrawer) return undefined;
    const onWinResize = () => setDrawerSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open, asDrawer]);

  const onDrawerResize = useCallback((next) => {
    setDrawerSize(clampWidth(next));
  }, []);

  const onDrawerResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : drawerSize);
      setDrawerSize(next);
      saveWidth(next);
    },
    [drawerSize]
  );

  useEffect(() => {
    if (!open) return;
    Promise.all([
      byRoom ? fetchSprNumberList() : Promise.resolve([]),
      byRoom ? fetchSprNumberCategories() : Promise.resolve([]),
      fetchDocExpLookups(),
      loadRuntimeSettings().catch(() => ({}))
    ])
      .then(([list, cats, lu, runtimeSettings]) => {
        const items = Array.isArray(list) ? list : pick(list, "items", "Items") || [];
        setNumbers(
          items
            .map((n) => ({
              value: pick(n, "idNumber", "IdNumber"),
              label: pick(n, "name", "Name") || String(pick(n, "idNumber", "IdNumber")),
              idCat: pick(n, "idNumberCat", "IdNumberCat") || 0,
              childCount: pick(n, "childCount", "ChildCount") || 0
            }))
            .filter((n) => n.value && !n.childCount)
        );
        setCategories(
          (Array.isArray(cats) ? cats : [])
            .map((c) => ({
              value: pick(c, "idNumberCat", "IdNumberCat") || pick(c, "value"),
              label: pick(c, "name", "Name") || pick(c, "label")
            }))
            .filter((c) => c.value)
        );
        const luNorm = normalizeLookups(lu);
        setLookups(luNorm);
        const draft = loadHeaderDraft();
        const today = dayjs().startOf("day");
        const nights = draft?.nights || 7;
        const beg = preset?.dateBeg ? dayjs(preset.dateBeg) : today;
        const end = preset?.dateEnd
          ? dayjs(preset.dateEnd)
          : beg.add(nights, "day");
        const bronStored = draft?.dateBron ? dayjs(draft.dateBron, "YYYY-MM-DD") : null;
        const dateBron =
          bronStored?.isValid() && !bronStored.isBefore(today, "day") ? bronStored : today;
        const room = numbersFromPreset(items, preset?.idNumber);
        skipHeaderSaveRef.current = true;
        form.setFieldsValue({
          idNumberCat: byRoom
            ? preset?.idNumberCat || room?.idCat || undefined
            : preset?.idNumberCat || draft?.idNumberCat || undefined,
          idNumber: byRoom ? preset?.idNumber || undefined : undefined,
          dateBeg: beg,
          dateEnd: end,
          timeBeg: parseTime(draft?.timeBeg, "14:00"),
          timeEnd: parseTime(draft?.timeEnd, "12:00"),
          dateBron,
          vidPutevki: pickLookupValue(
            luNorm.vidPutevki,
            draft?.vidPutevki,
            getSetting(runtimeSettings, "VID_PUT_DEFAULT", 0)
          ),
          vidPitania: pickLookupValue(
            luNorm.vidPitania,
            draft?.vidPitania,
            getSetting(runtimeSettings, "VID_PIT_DEFAULT", 0)
          ),
          komment: "",
          idClient: 0,
          idClientName: "",
          clients: [emptyGuest()],
          idSkidka: pickLookupValue(luNorm.skidki, draft?.idSkidka)
        });
        queueMicrotask(() => {
          skipHeaderSaveRef.current = false;
        });
        setHint(
          byRoom
            ? "Бронь на выбранный в шахматки номер"
            : "Номер подберётся из свободных выбранной категории"
        );
      })
      .catch((e) => message.error(e.message || "Ошибка загрузки"));
  }, [open, form, preset, byRoom]);

  const idNumberCat = Form.useWatch("idNumberCat", form);
  const idNumber = Form.useWatch("idNumber", form);
  const vidPutevki = Form.useWatch("vidPutevki", form);
  const vidPitania = Form.useWatch("vidPitania", form);
  const dateBeg = Form.useWatch("dateBeg", form);
  const dateEnd = Form.useWatch("dateEnd", form);
  const clients = Form.useWatch("clients", form);
  const idSkidka = Form.useWatch("idSkidka", form);
  const priceError = String(price?.error || "").trim();
  const canCreate = Boolean(
    idNumberCat && vidPutevki && vidPitania && (!byRoom || idNumber) && !priceError
  );
  const numberOptions = useMemo(() => {
    if (!idNumberCat) return numbers;
    return numbers.filter((n) => n.idCat === idNumberCat);
  }, [numbers, idNumberCat]);

  const priceKey = useMemo(
    () =>
      JSON.stringify({
        idNumberCat: idNumberCat || 0,
        idNumber: byRoom ? idNumber || 0 : 0,
        vidPutevki: vidPutevki || 0,
        vidPitania: vidPitania || 0,
        dateBeg: dateBeg?.isValid?.() ? dateBeg.format("YYYY-MM-DD") : "",
        dateEnd: dateEnd?.isValid?.() ? dateEnd.format("YYYY-MM-DD") : "",
        idSkidka: idSkidka || 0,
        clients: (clients || []).map((c) => ({
          vidMesta: c?.vidMesta || 0,
          vidLech: c?.vidLech || 0,
          isBaby: c?.isBaby ? 1 : 0
        }))
      }),
    [idNumberCat, idNumber, byRoom, vidPutevki, vidPitania, dateBeg, dateEnd, idSkidka, clients]
  );

  const guestCount = Array.isArray(clients) ? clients.length : 1;

  useEffect(() => {
    if (!open || byRoom) return undefined;
    if (!dateBeg?.isValid?.() || !dateEnd?.isValid?.()) return undefined;
    const beg = dateBeg.format("YYYY-MM-DD");
    const end = dateEnd.format("YYYY-MM-DD");
    if (dateEnd.isBefore(dateBeg, "day")) {
      setCategories([]);
      return undefined;
    }

    const ac = new AbortController();
    const t = setTimeout(() => {
      fetchBronFreeCategories({ dateBeg: beg, dateEnd: end, cntMest: Math.max(1, guestCount) }, ac.signal)
        .then((rows) => {
          const list = Array.isArray(rows) ? rows : [];
          const opts = list
            .map((c) => {
              const value = pick(c, "id", "Id", "idNumberCat", "IdNumberCat");
              const name = pick(c, "name", "Name") || "";
              const cnt = Number(pick(c, "cnt", "Cnt")) || 0;
              return {
                value,
                label: cnt ? `${name} - ${cnt}` : name
              };
            })
            .filter((c) => c.value);
          setCategories(opts);
          const current = form.getFieldValue("idNumberCat");
          if (current && opts.some((o) => Number(o.value) === Number(current))) return;
          skipHeaderSaveRef.current = true;
          form.setFieldValue("idNumberCat", opts[0]?.value);
          queueMicrotask(() => {
            skipHeaderSaveRef.current = false;
          });
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          setCategories([]);
        });
    }, 280);

    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [open, byRoom, dateBeg, dateEnd, guestCount, form]);

  useEffect(() => {
    if (!open) {
      setPrice(emptyPrice());
      setPriceLoading(false);
      return undefined;
    }
    const parsed = JSON.parse(priceKey);
    if (
      !parsed.idNumberCat ||
      !parsed.vidPutevki ||
      !parsed.vidPitania ||
      !parsed.dateBeg ||
      !parsed.dateEnd ||
      (byRoom && !parsed.idNumber)
    ) {
      setPrice(emptyPrice());
      setPriceLoading(false);
      return undefined;
    }

    const ac = new AbortController();
    const t = setTimeout(() => {
      setPriceLoading(true);
      docExpSellBookPreview(
        {
          idNumberCat: parsed.idNumberCat,
          ...(parsed.idNumber ? { idNumber: parsed.idNumber } : {}),
          dateBeg: parsed.dateBeg,
          dateEnd: parsed.dateEnd,
          vidPutevki: parsed.vidPutevki,
          vidPitania: parsed.vidPitania,
          idSkidka: parsed.idSkidka || 0,
          clients: parsed.clients
        },
        ac.signal
      )
        .then((res) => {
          if (!ac.signal.aborted) setPrice(normalizePrice(res));
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          if (!ac.signal.aborted) {
            setPrice({
              ...emptyPrice(),
              error: e.message || "Не удалось рассчитать стоимость"
            });
          }
        })
        .finally(() => {
          if (!ac.signal.aborted) setPriceLoading(false);
        });
    }, 280);

    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [open, priceKey, byRoom]);

  const persistHeader = useCallback(() => {
    saveHeaderDraft(form.getFieldsValue());
  }, [form]);

  const refreshFree = async () => {
    if (!byRoom) return;
    const values = form.getFieldsValue();
    if (!values.idNumber || !values.dateBeg || !values.dateEnd) return;
    try {
      const res = await fetchBronFreeMest({
        idNumber: values.idNumber,
        dateBeg: values.dateBeg.format("YYYY-MM-DD"),
        dateEnd: values.dateEnd.format("YYYY-MM-DD")
      });
      setHint(`Свободно основных мест: ${pick(res, "freeMest", "FreeMest") ?? 0}`);
    } catch {
      /* keep previous hint */
    }
  };

  const onFormValuesChange = (changedValues) => {
    refreshFree();
    if (skipHeaderSaveRef.current) return;
    const keys = Object.keys(changedValues || {});
    if (!keys.some((k) => HEADER_FIELDS.has(k))) return;
    clearTimeout(headerSaveTimer.current);
    headerSaveTimer.current = setTimeout(persistHeader, 400);
  };

  const onOk = async () => {
    if (priceError) return;
    try {
      const values = await form.validateFields();
      const dateBeg = values.dateBeg
        .startOf("day")
        .hour(values.timeBeg.hour())
        .minute(values.timeBeg.minute());
      const dateEnd = values.dateEnd
        .startOf("day")
        .hour(values.timeEnd.hour())
        .minute(values.timeEnd.minute());
      setSubmitting(true);
      const body = {
        idNumberCat: values.idNumberCat || 0,
        dateBeg: toApiDateTime(dateBeg),
        dateEnd: toApiDateTime(dateEnd),
        dateBron: toApiDate(values.dateBron),
        vidPutevki: values.vidPutevki || 0,
        vidPitania: values.vidPitania || 0,
        idSkidka: values.idSkidka || 0,
        komment: values.komment || "",
        idClient: values.idClient || 0,
        clients: (values.clients || []).map((c) => ({
          name: c.name || "",
          phone: c.phone || "",
          idPred: c.idPred || 0,
          vidMesta: c.vidMesta || 0,
          vidLech: c.vidLech || 0,
          isBaby: c.isBaby ? 1 : 0,
          komment: c.komment || "",
          dateBird: c.isBaby ? ageYearsToDateBird(c.age) : null
        }))
      };
      if (byRoom && values.idNumber) body.idNumber = values.idNumber;
      const saved = await docExpSellBook(body);
      saveHeaderDraft(values);
      message.success(
        byRoom ? "Путёвка создана и размещена" : "Путёвка создана, номер подобран и размещён"
      );
      onDone?.(saved);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || "Не удалось создать");
    } finally {
      setSubmitting(false);
    }
  };

  const title = byRoom ? "Продать (номер из шахматки)" : "Продажа + бронь";
  const requiredMark = (label, { required }) => (
    <>
      {required ? <span className="sell-book__star">*</span> : null}
      {label}
    </>
  );

  const drawerForm = (
    <Form
      form={form}
      layout="vertical"
      className="sell-book-form"
      colon={false}
      requiredMark={requiredMark}
      onValuesChange={onFormValuesChange}
    >
      <div className="sell-book__dates">
        <Form.Item
          name="dateBeg"
          label="Заезд"
          className="sell-book__date-beg"
          rules={[{ required: true, message: "Укажите заезд" }]}
        >
          <DatePicker format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        <Form.Item
          name="timeBeg"
          className="sell-book__time-beg"
          rules={[{ required: true, message: "Укажите время" }]}
        >
          <TimePicker format="HH:mm" allowClear={false} needConfirm={false} />
        </Form.Item>
        <Form.Item
          name="dateEnd"
          label="Выезд"
          className="sell-book__date-end"
          rules={[{ required: true, message: "Укажите выезд" }]}
        >
          <DatePicker format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        <Form.Item
          name="timeEnd"
          className="sell-book__time-end"
          rules={[{ required: true, message: "Укажите время" }]}
        >
          <TimePicker format="HH:mm" allowClear={false} needConfirm={false} />
        </Form.Item>
        <Form.Item name="dateBron" label="Бронь до" className="sell-book__date-bron">
          <DatePicker format="DD.MM.YYYY" />
        </Form.Item>
        <Form.Item name="idSkidka" className="sell-book__skidka">
          <Select
            allowClear
            placeholder="Вид скидки"
            showSearch
            optionFilterProp="label"
            options={(lookups.skidki || []).filter((o) => o.value)}
          />
        </Form.Item>
      </div>
      <Form.Item
        name="idNumberCat"
        label={
          <span className="sell-book__label-with-hint">
            Категория
            {!byRoom ? (
              <Tooltip
                title="Свободный номер выбранной категории подберётся автоматически на указанный период"
                placement="top"
              >
                <span
                  className="sell-book__label-hint"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <img src={infoSrc} alt="" />
                </span>
              </Tooltip>
            ) : null}
          </span>
        }
        rules={[{ required: true, message: "Укажите категорию" }]}
      >
        <Select showSearch optionFilterProp="label" options={categories} disabled={byRoom} />
      </Form.Item>
      {byRoom ? (
        <Form.Item name="idNumber" label="Номер" rules={[{ required: true, message: "Номер" }]}>
          <Select showSearch optionFilterProp="label" options={numberOptions} />
        </Form.Item>
      ) : null}
      <div className="sell-book__row">
        <Form.Item name="vidPutevki" rules={[requiredSelect("Укажите вид путёвки")]}>
          <Select
            allowClear
            placeholder="Вид путёвки"
            options={(lookups.vidPutevki || []).filter((o) => o.value)}
          />
        </Form.Item>
        <Form.Item name="vidPitania" rules={[requiredSelect("Укажите питание")]}>
          <Select
            allowClear
            placeholder="Питание"
            options={(lookups.vidPitania || []).filter((o) => o.value)}
          />
        </Form.Item>
      </div>
      <Form.Item name="idClient" hidden><InputNumber /></Form.Item>
      <Form.Item name="idClientName" hidden><Input /></Form.Item>
      <div className="sell-book__pred-row">
        <PredSelect
          value={idClientWatch || 0}
          label={idClientNameWatch}
          placeholder="Контрагент (заказчик)"
          onCreate={() => setPredDialog({ idPred: null })}
          onChange={async (id) => {
            if (!id) {
              form.setFieldsValue({ idClient: 0, idClientName: "" });
              return;
            }
            try {
              const pred = await fetchSprPred(id);
              form.setFieldsValue({ idClient: id, idClientName: pred?.name || "" });
            } catch (e) {
              message.error(e.message || "Не удалось загрузить контрагента");
            }
          }}
        />
        {idClientWatch ? (
          <Tooltip title="Открыть карточку контрагента">
            <Button icon={<FolderOpenOutlined />} aria-label="Открыть контрагента"
              onClick={() => setPredDialog({ idPred: idClientWatch })} />
          </Tooltip>
        ) : null}
      </div>
      <Form.Item name="komment">
        <Input placeholder="Комментарий" />
      </Form.Item>
      <Form.List name="clients">
        {(fields, { add, remove }) => (
          <GuestsPanel fields={fields} add={add} remove={remove} form={form} lookups={lookups}
            onOpenPred={(idPred, guestField) => setPredDialog({ idPred, guestField })} />
        )}
      </Form.List>
      <PriceSummary price={price} loading={priceLoading} />
    </Form>
  );

  const predDrawer = (
    <SprPred
      open={predDialog != null}
      idPred={predDialog?.idPred ?? null}
      isNew={predDialog?.idPred == null}
      onClose={() => setPredDialog(null)}
      onSaved={(saved) => {
        const id = Number(pick(saved, "idPred", "IdPred")) || predDialog?.idPred;
        const name = pick(saved, "name", "Name") || "";
        if (predDialog?.guestField != null) {
          const g = form.getFieldValue(["clients", predDialog.guestField]) || {};
          form.setFieldValue(["clients", predDialog.guestField], { ...g, ...guestFromPred({ ...saved, idPred: id }) });
        } else if (id) {
          form.setFieldsValue({ idClient: id, idClientName: name });
        }
        setPredDialog(null);
      }}
    />
  );

  const formBody = (
    <>
      <Form form={form} layout="vertical" onValuesChange={onFormValuesChange}>
        <Space wrap style={{ width: "100%" }} size={12}>
          <Form.Item name="dateBeg" label="Заезд" rules={[{ required: true }]}>
            <DatePicker format="DD.MM.YYYY" allowClear={false} />
          </Form.Item>
          <Form.Item name="dateEnd" label="Выезд" rules={[{ required: true }]}>
            <DatePicker format="DD.MM.YYYY" allowClear={false} />
          </Form.Item>
          <Form.Item name="dateBron" label="Бронь до">
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="idSkidka" label="Вид скидки" style={{ minWidth: 200 }}>
            <Select
              allowClear
              placeholder="Вид скидки"
              showSearch
              optionFilterProp="label"
              options={(lookups.skidki || []).filter((o) => o.value)}
            />
          </Form.Item>
        </Space>
        <Space wrap style={{ width: "100%" }} size={12}>
          <Form.Item name="timeBeg" label="Время" rules={[{ required: true }]}>
            <TimePicker format="HH:mm" allowClear={false} needConfirm={false} />
          </Form.Item>
          <Form.Item name="timeEnd" label="Время" rules={[{ required: true }]}>
            <TimePicker format="HH:mm" allowClear={false} needConfirm={false} />
          </Form.Item>
        </Space>
        <Space wrap style={{ width: "100%" }} size={12}>
          <Form.Item
            name="idNumberCat"
            label="Категория"
            rules={[{ required: true, message: "Укажите категорию" }]}
            style={{ minWidth: 200, flex: 1 }}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={categories}
              disabled={byRoom}
            />
          </Form.Item>
          {byRoom ? (
            <Form.Item
              name="idNumber"
              label="Номер"
              rules={[{ required: true, message: "Номер" }]}
              style={{ minWidth: 200, flex: 1 }}
            >
              <Select showSearch optionFilterProp="label" options={numberOptions} />
            </Form.Item>
          ) : null}
        </Space>
        {!byRoom ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Свободный номер выбранной категории подберётся автоматически на указанный период"
          />
        ) : null}
        <Space wrap style={{ width: "100%" }}>
          <Form.Item name="vidPutevki" label="Вид путёвки" style={{ minWidth: 200 }}>
            <Select allowClear options={lookups.vidPutevki} />
          </Form.Item>
          <Form.Item name="vidPitania" label="Питание" style={{ minWidth: 200 }}>
            <Select allowClear options={lookups.vidPitania} />
          </Form.Item>
        </Space>
        <Form.Item name="komment" label="Комментарий">
          <Input />
        </Form.Item>
        <Form.List name="clients">
          {(fields, { add, remove }) => (
            <GuestsPanel fields={fields} add={add} remove={remove} form={form} lookups={lookups}
              onOpenPred={(idPred, guestField) => setPredDialog({ idPred, guestField })} />
          )}
        </Form.List>
      </Form>
      <PriceSummary price={price} loading={priceLoading} />
      {hint ? <Alert style={{ marginTop: 12 }} type="info" showIcon message={hint} /> : null}
    </>
  );

  if (asDrawer) {
    return (
      <Drawer
        placement="right"
        title={title}
        size={drawerSize}
        maxSize={maxAllowedWidth()}
        open={open}
        onClose={onCancel}
        destroyOnClose
        maskClosable={!submitting}
        keyboard={!submitting}
        rootClassName="doc-exp-sell-book-drawer"
        closeIcon={
          <span className="doc-exp-sell-book-drawer__close">
            <img src={xSrc} alt="" />
          </span>
        }
        resizable={{
          onResize: onDrawerResize,
          onResizeEnd: onDrawerResizeEnd
        }}
        footer={
          <div className="doc-exp-sell-book-drawer__footer">
            <Button
              type="primary"
              className="doc-exp-sell-book-drawer__create"
              loading={submitting}
              disabled={!canCreate}
              title={priceError || undefined}
              onClick={onOk}
            >
              Создать
            </Button>
            <Button className="doc-exp-sell-book-drawer__cancel" onClick={onCancel} disabled={submitting}>
              Отмена
            </Button>
          </div>
        }
      >
        {drawerForm}
        {predDrawer}
      </Drawer>
    );
  }

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={submitting}
      okButtonProps={{ disabled: !canCreate, title: priceError || undefined }}
      okText="Создать"
      width={640}
      destroyOnClose
    >
      {formBody}
      {predDrawer}
    </Modal>
  );
};

function numbersFromPreset(items, idNumber) {
  if (!idNumber) return null;
  const n = items.find((x) => pick(x, "idNumber", "IdNumber") === idNumber);
  if (!n) return null;
  return {
    idCat: pick(n, "idNumberCat", "IdNumberCat") || 0
  };
}

export default DocExpSellBookModal;
